import { useState } from "react";
import axios from "axios";
import { Mail, CheckCircle, Send } from "lucide-react";
import { trackEvent } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewsletterSignup
 * Compact email capture form. Pass `variant="inline"` (default) or `variant="card"` for boxed styling.
 */
export default function NewsletterSignup({ variant = "inline", source = "website" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source });
      setStatus("sent");
      trackEvent("newsletter_subscribe", { source });
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  if (variant === "card") {
    return (
      <div
        data-testid="newsletter-card"
        className="glass-card p-8 md:p-10 text-center max-w-2xl mx-auto"
      >
        <div className="w-12 h-12 mx-auto mb-5 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
          <Mail size={22} className="text-blue-400" />
        </div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">
          Stay in the Loop
        </p>
        <h3
          className="text-2xl md:text-3xl tracking-tight font-medium mb-3"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Smart Home & Tech Tips,<br className="hidden md:block" /> Twice a Week
        </h3>
        <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
          Join the free newsletter from Southwest Florida's top smart home & TV mounting experts. No spam — unsubscribe anytime.
        </p>
        <SignupForm
          email={email}
          setEmail={setEmail}
          status={status}
          onSubmit={handleSubmit}
          testid="newsletter-card-form"
        />
        <p className="text-[11px] text-slate-600 mt-4 tracking-wide">
          By subscribing you agree to receive occasional emails. Your info is never shared.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="newsletter-inline"
      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6 rounded-xl bg-white/[0.02] border border-white/10"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-blue-600/15 flex items-center justify-center flex-shrink-0">
          <Mail size={16} className="text-blue-400" />
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">Get tips in your inbox</p>
          <p className="text-slate-400 text-xs truncate">Twice-weekly smart home & tech insights.</p>
        </div>
      </div>
      <SignupForm
        email={email}
        setEmail={setEmail}
        status={status}
        onSubmit={handleSubmit}
        testid="newsletter-inline-form"
      />
    </div>
  );
}

function SignupForm({ email, setEmail, status, onSubmit, testid }) {
  if (status === "sent") {
    return (
      <div
        data-testid={`${testid}-success`}
        className="flex items-center justify-center gap-2 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg px-4 py-3 text-sm font-medium w-full sm:w-auto sm:min-w-[360px]"
      >
        <CheckCircle size={16} /> You're subscribed! Watch your inbox.
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={testid}
      className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:min-w-[360px]"
    >
      <input
        type="email"
        name="email"
        autoComplete="email"
        inputMode="email"
        aria-label="Email address for newsletter"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        data-testid={`${testid}-input`}
        className="flex-1 bg-white/5 border border-white/10 focus:border-blue-500/50 focus:outline-none rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors"
      />
      <button
        type="submit"
        data-testid={`${testid}-submit`}
        disabled={status === "sending"}
        className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-medium px-5 py-3 rounded-lg transition-all btn-glow text-sm whitespace-nowrap"
      >
        {status === "sending" ? "Subscribing..." : (
          <>Subscribe <Send size={14} /></>
        )}
      </button>
      {status === "error" && (
        <span className="text-rose-400 text-xs mt-2 sm:ml-2">Couldn't subscribe. Try again?</span>
      )}
    </form>
  );
}
